import React, { useState, useRef, useContext } from 'react';
import useWindowDimensions from "../hooks/useWindowDimensions";
import CollapseContext from "../hooks/CollapseContext";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import Autocomplete from '@mui/material/Autocomplete';
import { TextField } from '@mui/material';

import './datePicker.css';

import { LanguageContext } from '../context/LanguageContext';
import commonTranslations from '../languages/common.json';

export default function CustomDatePicker({ handleTimeRange, initialRange, isLoading }) {
  dayjs.extend(relativeTime);

  const [inputValue, setInputValue] = useState(initialRange.inputValue);
  const [selectedTimeUnit, setSelectedTimeUnit] = useState(initialRange.timeUnit);
  const [startDate, setStartDate] = useState(dayjs.unix(initialRange.tstart));
  const [endDate, setEndDate] = useState(dayjs.unix(initialRange.tstop));
  const [hasChanged, setHasChanged] = useState(false);

  const tstart = useRef(dayjs().subtract(1, 'day'));
  const tstop = useRef(dayjs());

  const { lang } = useContext(LanguageContext);
  const t = commonTranslations[lang].common.datePicker;

  const handleStartChange = (newValue) => {
    const dateTime = dayjs(newValue);
    tstart.current = dateTime;
    setStartDate(dateTime);
  };

  const handleEndChange = (newValue) => {
    const dateTime = dayjs(newValue);
    tstop.current = dateTime;
    setEndDate(dateTime);
  };

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
    setHasChanged(true);
  };

  const handleTimeUnitChange = (event, newValue) => {
    setSelectedTimeUnit(newValue);
    setHasChanged(true);
  };

  const handleUpdate = () => {
    if (hasChanged === true) {
      const date = dayjs().subtract(inputValue, selectedTimeUnit);
      const stopdate = dayjs();
      tstart.current = date;
      tstop.current = stopdate;
      setStartDate(date);
      setEndDate(stopdate);
      setHasChanged(false);
    }

    const startEpochTime = tstart.current.unix();
    const endEpochTime = tstop.current.unix();
    handleTimeRange(startEpochTime, endEpochTime);
  };

  const dims = useWindowDimensions();
  const open = useContext(CollapseContext);
  const maxWidth = (open) ? (`${dims.width - 90}px`) : (`${dims.width - 270}px`);

  return (
    <div
      className={`time-range-container ${open ? 'cdp--open' : 'cdp--closed'}`}
      style={{ '--cdp-max-width': maxWidth }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <span className='text-label'>{t.from}</span>

        <DateTimePicker
          value={startDate}
          format="DD/MM/YYYY HH:mm"
          onChange={handleStartChange}
          slots={{ openPickerIcon: CalendarMonthIcon }}
          slotProps={{
            textField: {
              className: 'cdp-datetime-field',
              variant: 'outlined',
              size: 'small',
            },
          }}
        />

        <span className='text-label'>{t.to}</span>

        <DateTimePicker
          value={endDate}
          format="DD/MM/YYYY HH:mm"
          onChange={handleEndChange}
          slots={{ openPickerIcon: CalendarMonthIcon }}
          slotProps={{
            textField: {
              className: 'cdp-datetime-field',
              variant: 'outlined',
              size: 'small',
            },
          }}
        />
      </LocalizationProvider>

      <span className='text-label'>{t.last}</span>

      <input
        className='input-field'
        type="number"
        value={inputValue}
        min="1"
        onChange={handleInputChange}
        size={String(inputValue).length}
      />

      <Autocomplete
        className="cdp-unit"
        disableClearable
        options={Object.keys(t.units)}
        getOptionLabel={(opt) => t.units[opt]}
        value={selectedTimeUnit}
        onChange={handleTimeUnitChange}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            className="cdp-unit-field"
          />
        )}
      />

      <button
        className='btn edit-btn cdp-update-btn'
        onClick={handleUpdate}
        disabled={isLoading}
      >
        {t.update}
      </button>
    </div>
  );
}
